import React, { useState, useEffect, useCallback } from 'react';
import { Authenticator, View, Heading, Card, Text, Button, Flex, Image, Grid, Tabs, Loader } from '@aws-amplify/ui-react';
import { getUrl, uploadData } from 'aws-amplify/storage';
import { generateClient } from 'aws-amplify/api';
import { useNavigate } from 'react-router-dom';
import { listBookOrders, listDocumentRequests, getBook, listExchangeRates, listEnrollments } from '../graphql/queries';
// INYECCIÓN QUIRÚRGICA: Añadida createNotification
import { updateDocumentRequest, deleteDocumentRequest, deleteBookOrder, createNotification } from '../graphql/mutations';
// LIMPIEZA: Se eliminó FiCamera para evitar el warning de ESLint
import { FiBookOpen, FiLogOut, FiActivity, FiEye, FiDownload, FiClock, FiFileText, FiDollarSign, FiX, FiTrash2, FiPlayCircle } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import logo from '../logo.png'; 
import './UserProfile.css';
import PromoBanner from '../components/PromoBanner';

// 1. Datos de pago actualizados
const DATOS_PAGO_MOVIL = {
    banco: "0102 (BDV) Banco de Venezuela",
    rif: "V-16.129.666",
    telefono: "0424-300-9284"
};

const UserProfilePage = () => {
    const navigate = useNavigate();
    return (
        <View className="dashboard-wrapper">
            <Authenticator>
                {({ signOut, user }) => (
                    <ProfileContent user={user} signOut={signOut} navigate={navigate} />
                )}
            </Authenticator>
        </View>
    );
}

const ProfileContent = ({ user, signOut, navigate }) => {
    const client = generateClient();
    const [myBooks, setMyBooks] = useState([]);
    const [myDocs, setMyDocs] = useState([]);
    const [myCourses, setMyCourses] = useState([]); 
    const [profileImg, setProfileImg] = useState(null);
    const [bcvRate, setBcvRate] = useState(0);

    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [selectedDoc, setSelectedDoc] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [receiptFile, setReceiptFile] = useState(null);

    const [bankName, setBankName] = useState('');
    const [refNumber, setRefNumber] = useState('');

    const [showAvatarModal, setShowAvatarModal] = useState(false);
    const [uploadingAvatar, setUploadingAvatar] = useState(false);

    const avataresPredefinidos = [
        "https://cdn-icons-png.flaticon.com/512/3135/3135715.png",
        "https://cdn-icons-png.flaticon.com/512/3135/3135768.png",
        "https://cdn-icons-png.flaticon.com/512/4140/4140047.png",
        "https://cdn-icons-png.flaticon.com/512/4139/4139981.png",
        "https://cdn-icons-png.flaticon.com/512/6997/6997662.png",
        "https://cdn-icons-png.flaticon.com/512/4140/4140048.png"
    ];

    const loadUserData = useCallback(async () => {
        if (!user) return;
        const rawEmail = user.signInDetails?.loginId || user.attributes?.email || user.username || "";
        const emailLower = rawEmail.toLowerCase().trim();
        
        try {
            const rateRes = await client.graphql({ query: listExchangeRates, authMode: 'apiKey' });
            if (rateRes.data.listExchangeRates.items.length > 0) {
                setBcvRate(rateRes.data.listExchangeRates.items[0].value);
            }

            try {
                const userKey = user.userId || user.username;
                const imgRes = await getUrl({ 
                    path: `public/avatars/${userKey}_profile.jpg`,
                    options: { validateObjectExistence: true }
                });
                setProfileImg(imgRes.url.toString());
            } catch (e) { 
                console.log("Cargando imagen predeterminada...");
            }

            const [bookRes, docRes, courseRes] = await Promise.all([
                client.graphql({ query: listBookOrders, authMode: 'userPool' }),
                client.graphql({ query: listDocumentRequests, authMode: 'userPool' }),
                client.graphql({ query: listEnrollments, authMode: 'userPool' })
            ]);

            setMyBooks((bookRes.data.listBookOrders.items || []).filter(b => (b.customerEmail || "").toLowerCase().trim() === emailLower));
            setMyDocs((docRes.data.listDocumentRequests.items || []).filter(d => (d.customerEmail || "").toLowerCase().trim() === emailLower));
            setMyCourses((courseRes.data.listEnrollments.items || []).filter(c => (c.userEmail || "").toLowerCase().trim() === emailLower));

        } catch (err) { console.error("❌ Error AWS:", err); }
    }, [user, client]);

    useEffect(() => { loadUserData(); }, [loadUserData]);

    const handleUploadAvatar = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        setUploadingAvatar(true);
        try {
            const userKey = user.userId || user.username;
            const customPath = `public/avatars/${userKey}_profile.jpg`;
            
            await uploadData({
                path: customPath,
                data: file,
                options: { contentType: file.type }
            }).result;
            
            const imgRes = await getUrl({ path: customPath });
            setProfileImg(imgRes.url.toString());
            setShowAvatarModal(false);
            alert("✅ Foto actualizada con éxito");
        } catch (err) {
            console.error(err);
            alert("Error de permisos en S3.");
        } finally {
            setUploadingAvatar(false);
        }
    };

    const handleSelectIcon = async (url) => {
        setUploadingAvatar(true);
        try {
            const userKey = user.userId || user.username;
            const response = await fetch(url);
            const blob = await response.blob();
            const customPath = `public/avatars/${userKey}_profile.jpg`;
            
            await uploadData({
                path: customPath,
                data: blob,
                options: { contentType: 'image/png' }
            }).result;

            setProfileImg(url);
            setShowAvatarModal(false);
        } catch (err) {
            alert("Error al guardar el icono.");
        } finally {
            setUploadingAvatar(false);
        }
    };

    const handleDeleteDoc = async (id) => {
        if (!window.confirm("¿Deseas eliminar esta solicitud de trámite?")) return;
        try {
            await client.graphql({ query: deleteDocumentRequest, variables: { input: { id } }, authMode: 'userPool' });
            loadUserData();
        } catch (e) { alert("No se pudo eliminar."); }
    };

    const handleDeleteBookOrder = async (id) => {
        if (!window.confirm("¿Deseas eliminar este registro de libro?")) return;
        try {
            await client.graphql({ query: deleteBookOrder, variables: { input: { id } }, authMode: 'userPool' });
            loadUserData();
        } catch (e) { alert("No se pudo eliminar."); }
    };

    // FUNCIÓN EVOLUCIONADA: Integración de notificaciones sin simplificar
    const handleConfirmPayment = async () => {
        if (!receiptFile || !refNumber || !bankName) return alert("Completa todos los campos.");
        setUploading(true);
        try {
            const fileName = `receipts/doc-${selectedDoc.id}-${Date.now()}.jpg`;
            
            // Subida a S3
            await uploadData({ path: `public/${fileName}`, data: receiptFile, options: { contentType: receiptFile.type } }).result;
            
            // 1. Actualización de estado del trámite
            await client.graphql({
                query: updateDocumentRequest,
                variables: { input: { id: selectedDoc.id, status: 'PAGADO', referenceNumber: refNumber, bankName: bankName, receiptKey: fileName } },
                authMode: 'userPool', 
            });

            // 2. DISPARO DE NOTIFICACIÓN PARA EL ADMIN (Sistema Scarvaci 2026)
            try {
                await client.graphql({
                    query: createNotification,
                    variables: {
                        input: {
                            recipientEmail: "giovanniscarvaciyasociados@gmail.com",
                            title: "💰 NUEVO PAGO DE TRÁMITE",
                            message: `El cliente ${user.signInDetails?.loginId || user.attributes?.email} reportó pago para: ${selectedDoc.areaLegal}. Ref: ${refNumber}`,
                            type: "PAYMENT",
                            isRead: false,
                            relatedId: selectedDoc.id
                        }
                    },
                    authMode: 'userPool'
                });
            } catch (notifErr) {
                console.error("Error silencioso al crear notificación:", notifErr);
            }

            alert("✅ Pago reportado con éxito. El administrador ha sido notificado.");
            setShowPaymentModal(false);
            loadUserData();
        } catch (e) { 
            console.error(e);
            alert("Error al procesar el pago."); 
        } finally { 
            setUploading(false); 
        }
    };

    const handleDownloadPDF = async (bookOrder) => {
        try {
            const bookRes = await client.graphql({ query: getBook, variables: { id: bookOrder.bookID }, authMode: 'apiKey' });
            let pdfPath = bookRes.data.getBook?.pdfKey;
            if (!pdfPath) return alert("PDF no disponible.");
            const cleanPath = pdfPath.startsWith('public/') ? pdfPath : `public/${pdfPath}`;
            const signedUrl = await getUrl({ path: cleanPath });
            window.open(signedUrl.url.toString(), '_blank');
        } catch (err) { alert("Error en descarga."); }
    };

    const handleDownloadResult = async (resultKey) => {
        try {
            const pathForDownload = resultKey.startsWith('public/') ? resultKey : `public/${resultKey}`;
            const signedUrl = await getUrl({ path: pathForDownload });
            window.open(signedUrl.url.toString(), '_blank');
        } catch (err) { alert("Error al abrir resultado."); }
    };

    return (
        <View className="main-content fade-in">
            <Flex className="glass-header" justifyContent="space-between" alignItems="center" padding="15px">
                <Flex alignItems="center" gap="15px">
                    <div style={{ position: 'relative' }}>
                        <Image src={logo} height="70px" width="70px" borderRadius="50%" style={{ objectFit: 'cover', border: '3px solid #D4AF37' }} />
                    </div>
                    <View>
                        <Heading level={4} className="text-dark">Giovanni Scarvaci & Asociados</Heading>
                        <Text className="text-gold">{user.signInDetails?.loginId || user.attributes?.email}</Text>
                    </View>
                </Flex>
                <Button onClick={signOut} className="btn-logout"><FiLogOut /> Salir</Button>
            </Flex>

            <View padding="20px 20px 0 20px">
                <PromoBanner /> 
            </View>

            {showAvatarModal && (
                <div className="modal-payment-overlay fade-in">
                    <div className="modal-payment-card" style={{ maxWidth: '400px' }}>
                        <button className="close-x" onClick={() => setShowAvatarModal(false)}><FiX /></button>
                        <Heading level={4} textAlign="center" marginBottom="20px">Personalizar Perfil</Heading>
                        <Text marginBottom="10px" fontWeight="bold">1. Subir tu propia foto:</Text>
                        <input type="file" accept="image/*" onChange={handleUploadAvatar} style={{ marginBottom: '20px' }} />
                        <Text marginBottom="10px" fontWeight="bold">2. O elige un icono:</Text>
                        <Flex gap="15px" justifyContent="center" wrap="wrap" marginBottom="20px">
                            {avataresPredefinidos.map((url, idx) => (
                                <Image key={idx} src={url} height="55px" width="55px" style={{ cursor: 'pointer', border: profileImg === url ? '2px solid #D4AF37' : '1px solid #eee', borderRadius: '50%', padding: '2px' }} onClick={() => handleSelectIcon(url)} />
                            ))}
                        </Flex>
                        {uploadingAvatar && <Loader marginBottom="15px" />}
                        <Button width="100%" onClick={() => setShowAvatarModal(false)}>CANCELAR</Button>
                    </div>
                </div>
            )}

            <Grid templateColumns={{ base: '1fr', medium: '1fr 1fr 1fr' }} gap="20px" padding="20px">
                <Card className="stat-card"><FiBookOpen size="24px" color="#D4AF37"/><Heading level={3}>{myBooks.length}</Heading><Text>Libros Adquiridos</Text></Card>
                <Card className="stat-card"><FiActivity size="24px" color="#D4AF37"/><Heading level={3}>{myDocs.length}</Heading><Text>Trámites Legales</Text></Card>
                <Card className="stat-card"><FiPlayCircle size="24px" color="#D4AF37"/><Heading level={3}>{myCourses.length}</Heading><Text>Cursos Inscritos</Text></Card>
            </Grid>

            <View className="amplify-tabs" padding="10px">
                <Tabs items={[
                    { label: '📚 BIBLIOTECA', value: 'books', content: (
                        <View padding="20px">
                            {myBooks.length > 0 ? (
                                <Grid templateColumns={{ base: '1fr', medium: '1fr 1fr' }} gap="15px">
                                    {myBooks.map(b => (
                                        <Card key={b.id} className="book-card-premium">
                                            <Flex justifyContent="space-between" alignItems="flex-start">
                                                <View><Heading level={5} style={{color: '#D4AF37'}}>{b.bookTitle}</Heading></View>
                                                {b.status !== 'APROBADO' && <FiTrash2 color="#ff4d4d" cursor="pointer" onClick={() => handleDeleteBookOrder(b.id)} />}
                                            </Flex>
                                            {b.status === 'APROBADO' ? (
                                                <Flex gap="10px" marginTop="15px">
                                                    {(b.category === 'ONLINE' || b.category === 'DIGITAL') && <Button size="small" onClick={() => navigate(`/reader/${b.id}`)} style={{ backgroundColor: '#D4AF37', color: 'white' }}><FiEye /> Leer Online</Button>}
                                                    {(b.category === 'PDF' || b.category === 'DIGITAL') && <Button size="small" onClick={() => handleDownloadPDF(b)} style={{ backgroundColor: '#001f3f', color: 'white', border: '1px solid #D4AF37' }}><FiDownload /> PDF</Button>}
                                                </Flex>
                                            ) : <Text style={{color: '#ffd814', marginTop: '10px'}}><FiClock /> {b.status}</Text>}
                                        </Card>
                                    ))}
                                </Grid>
                            ) : <Text textAlign="center">No hay libros.</Text>}
                        </View>
                    )},
                    { label: '🎓 ACADEMIA', value: 'courses', content: (
                        <View padding="20px">
                            {myCourses.length > 0 ? (
                                <Grid templateColumns={{ base: '1fr', medium: '1fr 1fr' }} gap="15px">
                                    {myCourses.map(c => (
                                        <Card key={c.id} className="book-card-premium" style={{ borderLeft: '4px solid #D4AF37' }}>
                                            <Heading level={5} style={{color: '#fff'}}>{c.courseTitle}</Heading>
                                            {(c.status === 'APROBADO' || c.status === 'COMPLETADO' || c.status === 'ACTIVO') ? (
                                                <Button marginTop="15px" onClick={() => navigate(`/course-player/${c.courseId}`)} style={{ backgroundColor: '#D4AF37', color: '#000', fontWeight: 'bold' }}><FiPlayCircle /> ENTRAR</Button>
                                            ) : <Text color="#ffd814" marginTop="10px"><FiClock /> Pago en verificación</Text>}
                                        </Card>
                                    ))}
                                </Grid>
                            ) : <Text textAlign="center">No hay cursos.</Text>}
                        </View>
                    )},
                    { label: '⚖️ TRÁMITES', value: 'docs', content: (
                        <View padding="20px">
                            {myDocs.length > 0 ? (
                                <Grid gap="15px">
                                    {myDocs.map(d => (
                                        <Card key={d.id} className="document-card-user">
                                            <Flex justifyContent="space-between" alignItems="center">
                                                <View><Heading level={5}><FiFileText /> {d.areaLegal}</Heading></View>
                                                <Flex gap="10px" alignItems="center">
                                                    <div className={`status-badge-user ${d.status}`}>{d.status}</div>
                                                    {(d.status === 'SOLICITADO' || d.status === 'PENDIENTE_PAGO') && <FiTrash2 color="#ff4d4d" cursor="pointer" onClick={() => handleDeleteDoc(d.id)} />}
                                                </Flex>
                                            </Flex>
                                            <Text marginTop="10px"><strong>Descripción:</strong> {d.description}</Text>
                                            {d.priceUSD > 0 && <Flex alignItems="center" marginTop="10px" style={{color:'#D4AF37'}}><FiDollarSign /> <Text color="#D4AF37">Presupuesto: <strong>${d.priceUSD}</strong></Text></Flex>}
                                            {d.status === 'PENDIENTE_PAGO' && <Button marginTop="15px" width="100%" className="btn-pay" onClick={() => { setSelectedDoc(d); setShowPaymentModal(true); }}>💰 Pagar Móvil</Button>}
                                            {d.status === 'COMPLETADO' && d.resultUrl && <Button marginTop="15px" width="100%" style={{backgroundColor: '#28a745', color: 'white'}} onClick={() => handleDownloadResult(d.resultUrl)}>📥 Descargar</Button>}
                                        </Card>
                                    ))}
                                </Grid>
                            ) : <Text textAlign="center">No hay trámites.</Text>}
                        </View>
                    )}
                ]} />
            </View>

            {showPaymentModal && selectedDoc && (
                <div className="modal-payment-overlay fade-in">
                    <div className="modal-payment-card">
                        <button className="close-x" onClick={() => setShowPaymentModal(false)}><FiX /></button>
                        <Heading level={4} textAlign="center">Reportar Pago</Heading>
                        
                        <View padding="15px" backgroundColor="#f9f9f9" borderRadius="8px" marginTop="15px">
                            <Text fontWeight="bold" color="#001f3f">Datos para Pago Móvil:</Text>
                            <Text fontSize="small"><strong>Banco:</strong> {DATOS_PAGO_MOVIL.banco}</Text>
                            <Text fontSize="small"><strong>Cédula:</strong> {DATOS_PAGO_MOVIL.rif}</Text>
                            <Text fontSize="small"><strong>Teléfono:</strong> {DATOS_PAGO_MOVIL.telefono}</Text>
                        </View>

                        <div className="payment-amounts">
                            <p>Monto: <strong>${selectedDoc.priceUSD} USD</strong></p>
                            <h3 style={{color:'#D4AF37', margin:'10px 0'}}>Total: Bs. {(selectedDoc.priceUSD * bcvRate).toFixed(2)}</h3>
                        </div>

                        <input type="text" className="payment-input" placeholder="Banco emisor" value={bankName} onChange={(e) => setBankName(e.target.value)} style={{marginTop:'15px'}} />
                        <input type="text" className="payment-input" placeholder="Referencia (6 últimos dígitos)" value={refNumber} onChange={(e) => setRefNumber(e.target.value)} style={{marginTop:'10px'}} />
                        
                        <Text marginTop="15px" fontSize="small">Foto del Comprobante:</Text>
                        <input type="file" accept="image/*" onChange={(e) => setReceiptFile(e.target.files[0])} style={{marginTop:'5px'}} />
                        
                        <Button className="btn-pay-final" isLoading={uploading} onClick={handleConfirmPayment} marginTop="20px">ENVIAR REPORTE</Button>
                        <Button variation="link" width="100%" onClick={() => window.open(`https://wa.me/584243009284`, '_blank')}><FaWhatsapp /> Soporte</Button>
                    </div>
                </div>
            )}
        </View>
    );
};

export default UserProfilePage;